// src/pages/MaterialDetail.jsx - COMPLETE UPDATED VERSION
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Download, Eye, Calendar, User, FileText, ArrowLeft, Loader2, Share2,
  Clock, ChevronRight, GraduationCap, Bookmark, Star, File, AlertCircle, 
  ExternalLink, FileImage, Maximize2, X, Image as ImageIcon
} from 'lucide-react';
import { motion } from 'framer-motion';
import { publicAPI } from '../services/api';

const MaterialDetail = () => {
  const { materialId } = useParams();
  const navigate = useNavigate();
  
  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        const response = await publicAPI.getMaterialById(materialId);
        if (response.success) {
          setMaterial(response.data);
        } else {
          setError('Material not found');
        }
      } catch (err) {
        setError('Failed to load material details');
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [materialId]);

  const isImage = publicAPI.isImage(material);
  const canPreview = publicAPI.canPreview(material);

  const handleDownload = async () => {
    if (!material) return;
    
    setDownloading(true);

    try {
      const downloadUrl = publicAPI.getDownloadUrl(material._id);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', material.file?.originalName || material.title);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      await publicAPI.trackDownload(material._id);

      // Update UI optimistically
      setMaterial(prev => ({
        ...prev,
        downloads: (prev.downloads || 0) + 1
      }));

      // Show download notification
      if (window.addDownloadNotification) {
        window.addDownloadNotification(material);
      }

    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    } finally {
      setTimeout(() => setDownloading(false), 2000);
    }
  };

  const handlePreview = async () => {
    if (!material || !canPreview) {
      alert('Preview not available for this file type. Please download instead.');
      return;
    }
    
    setPreviewing(true);

    try {
      if (window.openFilePreview) {
        window.openFilePreview(material);
      } else if (isImage) {
        setShowFullImage(true);
      } else {
        const previewUrl = publicAPI.getPreviewUrl(material._id);
        window.open(previewUrl, '_blank', 'noopener,noreferrer');
      }

      await publicAPI.trackDownload(material._id);

      // Update views
      setMaterial(prev => ({
        ...prev,
        views: (prev.views || 0) + 1
      }));

      // Show preview notification
      if (window.addPreviewNotification) {
        window.addPreviewNotification(material);
      }

    } catch (error) {
      console.error('Preview failed:', error);
      alert('Preview failed. Please try again.');
    } finally {
      setTimeout(() => setPreviewing(false), 1000);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    } catch(e) {
      console.error('Share failed:', e);
    }
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-12 h-12 text-blue-600 animate-spin"/>
    </div>
  );
  
  if (error || !material) return (
    <div className="min-h-screen flex items-center justify-center text-red-600">
      {error || "Material not found"}
    </div>
  );

  return (
    <>
      {/* Full Image Modal (Telegram-style) */}
      {showFullImage && isImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[999999] bg-black flex items-center justify-center"
          onClick={() => setShowFullImage(false)}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close button */}
            <button
              onClick={() => setShowFullImage(false)}
              className="absolute top-4 right-4 z-10 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Download button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDownload();
              }}
              className="absolute top-4 left-4 z-10 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
            >
              <Download className="w-6 h-6" />
            </button>
            
            {/* Image */}
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={publicAPI.getPreviewUrl(material._id)}
              alt={material.title}
              className="max-w-full max-h-full object-contain cursor-zoom-out"
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Caption */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-6 pt-12">
              <div className="max-w-4xl mx-auto">
                <h3 className="text-white text-lg font-semibold mb-2">{material.title}</h3>
                <div className="flex items-center gap-4 text-white/80 text-sm">
                  <span>{material.file?.format?.toUpperCase()}</span>
                  <span>•</span>
                  <span>{formatFileSize(material.file?.size)}</span>
                  <span>•</span>
                  <span>{material.downloads || 0} downloads</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8">
        <div className="max-w-6xl mx-auto px-4">
          {/* Back Button */}
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-8 group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" /> 
            <span>Back</span>
          </button>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-xl p-8"
              >
                {/* Header */}
                <div className="flex justify-between items-start mb-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      {isImage ? (
                        <div className="p-3 bg-green-100 text-green-600 rounded-lg">
                          <FileImage className="w-6 h-6" />
                        </div>
                      ) : (
                        <div className="p-3 bg-blue-100 text-blue-600 rounded-lg">
                          <FileText className="w-6 h-6" />
                        </div>
                      )}
                      <div>
                        <h1 className="text-3xl font-bold text-gray-900">{material.title}</h1>
                        <p className="text-gray-600 mt-1">
                          {material.file?.format?.toUpperCase()} • {formatFileSize(material.file?.size)}
                        </p>
                      </div>
                    </div>
                    
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                        {material.type?.toUpperCase() || 'RESOURCE'}
                      </span>
                      {material.tags?.map((tag, index) => (
                        <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <button
                    onClick={handleShare}
                    className="p-3 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                    title="Share"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>

                {/* Image Preview (if image) */}
                {isImage && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">Preview</h3>
                      <button
                        onClick={() => setShowFullImage(true)}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <Maximize2 className="w-4 h-4" />
                        View Full Image
                      </button>
                    </div>
                    <div 
                      className="bg-gray-100 rounded-xl overflow-hidden border border-gray-200 cursor-pointer"
                      onClick={() => setShowFullImage(true)}
                    >
                      <img
                        src={publicAPI.getPreviewUrl(material._id)}
                        alt={material.title}
                        className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = `data:image/svg+xml,${encodeURIComponent(`
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                              <rect width="100" height="100" fill="#f3f4f6"/>
                              <text x="50" y="50" font-family="Arial" font-size="14" fill="#9ca3af" text-anchor="middle" dy=".3em">${material.file?.format?.toUpperCase() || 'IMAGE'}</text>
                            </svg>
                          `)}`;
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-4 mb-8">
                  <button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="px-6 py-3 bg-green-600 text-white rounded-xl font-semibold flex items-center gap-3 hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {downloading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Download className="w-5 h-5" />
                    )}
                    {downloading ? 'Downloading...' : 'Download'}
                  </button>
                  
                  {canPreview && (
                    <button
                      onClick={handlePreview}
                      disabled={previewing}
                      className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold flex items-center gap-3 hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {previewing ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : isImage ? (
                        <>
                          <Eye className="w-5 h-5" />
                          View Image
                        </>
                      ) : (
                        <>
                          <ExternalLink className="w-5 h-5" />
                          Preview
                        </>
                      )}
                      {previewing && 'Opening...'}
                    </button>
                  )}
                </div>

                {/* Description */}
                {material.description && (
                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Description</h3>
                    <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                      <p className="text-gray-600 whitespace-pre-line">{material.description}</p>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Details Card */}
              <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200">
                <h3 className="font-bold text-gray-900 mb-4 text-lg flex items-center gap-2">
                  <File className="w-5 h-5" />
                  File Details
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Type</span>
                    <span className="font-medium capitalize">{material.file?.format || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Size</span>
                    <span className="font-medium">{formatFileSize(material.file?.size)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Uploaded</span>
                    <span className="font-medium">{formatDate(material.createdAt)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Downloads</span>
                    <span className="font-medium">{material.downloads || 0}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600">Views</span>
                    <span className="font-medium">{material.views || 0}</span>
                  </div>
                </div>
              </div>

              {/* Teacher Card */}
              {material.teacher && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-200">
                  <h3 className="font-bold text-gray-900 mb-4 text-lg flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Uploaded By
                  </h3>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center overflow-hidden border-2 border-white shadow-sm">
                      {material.teacher.showAvatarPublicly !== false && material.teacher.profileImage?.url ? (
                        <img 
                          src={publicAPI.getFullImageUrl(material.teacher.profileImage.url)}
                          alt={material.teacher.fullName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-6 h-6 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{material.teacher.fullName}</h4>
                      <p className="text-sm text-gray-600">{material.teacher.email}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MaterialDetail;